import { useCallback, useEffect, useState } from 'react';

import formulaCkdepiPng from '@assets/formula_ckdepi.png';
import formulaCockcroftGaultPng from '@assets/formula_cockcroft_gault.png';
import formulaMdrdPng from '@assets/formula_mdrd.png';
import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { EEthnicity } from '@enums/Ethnicity';
import { EGender } from '@enums/Gender';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import BigNumber from 'bignumber.js';
import { CircleHelp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { useTaxaFiltracaoGlomerular } from './context';

const formSchema = z.object({
  weight: z.string().min(1, {
    message: errorsValidation.required,
  }),
  age: z.string().min(1, {
    message: errorsValidation.required,
  }),
  gender: z.enum(EGender, {
    message: errorsValidation.required,
  }),
  ethnicity: z.enum(EEthnicity, {
    message: errorsValidation.required,
  }),
  plasmaCreatinine: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function GlomerularFiltrationRate() {
  const [resultCockcroftGault, setResultCockcroftGault] = useState<number>(0);
  const [resultCKDEPI, setResultCKDEPI] = useState<number>(0);
  const [resultMDRD, setResultMDRD] = useState<number>(0);

  const {
    age,
    ethnicity,
    gender,
    plasmaCreatinine,
    setAge,
    setEthnicity,
    setGender,
    setPlasmaCreatinine,
    setWeight,
    weight,
  } = useTaxaFiltracaoGlomerular();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      weight: weight.toFixed(1),
      age: String(age),
      gender: gender,
      ethnicity,
      plasmaCreatinine: plasmaCreatinine.toFixed(1),
    },
  });

  const calculateCockcroftGault = useCallback((): void => {
    // FÓRMULA: [(140 - idade) * peso * k] / (72 * creatinina)
    // k = 0,85 se for mulher ou 1 se for homem
    const ageParsed = new BigNumber(age);
    const plasmaCreatinineParsed = new BigNumber(plasmaCreatinine);
    const weightParsed = new BigNumber(weight);
    const k =
      gender === EGender.Female ? new BigNumber(0.85) : new BigNumber(1);
    const stepOne = new BigNumber(140).minus(ageParsed);
    const stepTwo = stepOne.multipliedBy(weightParsed).multipliedBy(k);
    const stepThree = new BigNumber(72).multipliedBy(plasmaCreatinineParsed);
    const result = stepTwo.dividedBy(stepThree);
    setResultCockcroftGault(
      Number.isFinite(result.toNumber()) ? result.toNumber() : 0,
    );
  }, [age, gender, plasmaCreatinine, weight]);

  useEffect(() => {
    calculateCockcroftGault();
  }, [calculateCockcroftGault]);

  const calculateCKDEPI = useCallback((): void => {
    // FÓRMULA: 142 * min(creatinina / k, 1) elevado a A * max(creatinina / k, 1) elevado a -1,2 * 0,9938 elevado a idade * L
    // k = 0,7 se for mulher e 0,9 se for homem
    // A = -0,241 se for mulher e -0,302 se for homem
    // L = 1,012 se for mulher e 1 se for homem
    const plasmaCreatinineParsed = new BigNumber(plasmaCreatinine);
    const k =
      gender === EGender.Female ? new BigNumber(0.7) : new BigNumber(0.9);
    const a =
      gender === EGender.Female ? new BigNumber(-0.241) : new BigNumber(-0.302);
    const l =
      gender === EGender.Female ? new BigNumber(1.012) : new BigNumber(1);
    const creatinineMin = Math.pow(
      Math.min(plasmaCreatinineParsed.dividedBy(k).toNumber(), 1),
      a.toNumber(),
    );
    const creatinineMax = Math.pow(
      Math.max(plasmaCreatinineParsed.dividedBy(k).toNumber(), 1),
      -1.2,
    );
    const powAge = Math.pow(0.9938, age);
    const result = new BigNumber(142)
      .multipliedBy(creatinineMin)
      .multipliedBy(creatinineMax)
      .multipliedBy(powAge)
      .multipliedBy(l);
    setResultCKDEPI(Number.isFinite(result.toNumber()) ? result.toNumber() : 0);
  }, [age, gender, plasmaCreatinine]);

  useEffect(() => {
    calculateCKDEPI();
  }, [calculateCKDEPI]);

  const calculateMDRD = useCallback((): void => {
    // FÓRMULA: 175 * creatinina elevado a -1,154 * idade elevado a -0,203 * b * c
    // b = 1,212 se for negro e 1 se for outras
    // c = 0,742 se for mulher e 1 se for homem
    const plasmaCreatinineParsed = new BigNumber(plasmaCreatinine);
    const b =
      ethnicity === EEthnicity.Black ? new BigNumber(1.212) : new BigNumber(1);
    const c =
      gender === EGender.Female ? new BigNumber(0.742) : new BigNumber(1);
    const powCreatinine = Math.pow(plasmaCreatinineParsed.toNumber(), -1.154);
    const powAge = Math.pow(age, -0.203);
    const result = new BigNumber(175)
      .multipliedBy(powCreatinine)
      .multipliedBy(powAge)
      .multipliedBy(b)
      .multipliedBy(c);
    setResultMDRD(Number.isFinite(result.toNumber()) ? result.toNumber() : 0);
  }, [age, ethnicity, gender, plasmaCreatinine]);

  useEffect(() => {
    calculateMDRD();
  }, [calculateMDRD]);

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  const handleChangeAge = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setAge(valueParsed);
    },
    [setAge],
  );

  const handleChangeGender = useCallback(
    (value: EGender): void => {
      setGender(value);
    },
    [setGender],
  );

  const handleChangeEthnicity = useCallback(
    (value: EEthnicity): void => {
      setEthnicity(value);
    },
    [setEthnicity],
  );

  const handleChangePlasmaCreatinine = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setPlasmaCreatinine(valueParsed);
    },
    [setPlasmaCreatinine],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
      if (name === 'age' && value.age) {
        handleChangeAge(value.age);
      }
      if (name === 'gender' && value.gender) {
        handleChangeGender(value.gender);
      }
      if (name === 'ethnicity' && value.ethnicity) {
        handleChangeEthnicity(value.ethnicity);
      }
      if (name === 'plasmaCreatinine' && value.plasmaCreatinine) {
        handleChangePlasmaCreatinine(value.plasmaCreatinine);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div>
      <PageTitle hasBackButton title="Taxa filtração glomerular" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Peso (Kg)</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="age"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Idade (anos)</FormLabel>
                  <FormControl>
                    <NumericFormat
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sexo</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'Feminino',
                          value: EGender.Female,
                        },
                        {
                          label: 'Masculino',
                          value: EGender.Male,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ethnicity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Etnia</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'Negra',
                          value: EEthnicity.Black,
                        },
                        {
                          label: 'Outras',
                          value: EEthnicity.Others,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="plasmaCreatinine"
              render={({ field: { ref, ...rest } }) => (
                <FormItem className="flex flex-col items-center">
                  <FormLabel>Creatinina plasmática (mg/dL)</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator className="my-4" />

          <div className="flex flex-row items-center justify-center">
            <Label className="text-lg">Cockcroft & Gault</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-lg space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  Cockcroft & Gault
                </h1>
                <img alt="Cockcroft & Gault" src={formulaCockcroftGaultPng} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {resultCockcroftGault.toFixed(1)}
              </span>{' '}
            </p>
            <Label className="text-foreground text-sm font-light">mL/min</Label>
          </div>

          <div className="flex flex-row items-center justify-center">
            <Label className="text-lg">CKD-EPI</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-lg space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  CKD-EPI
                </h1>
                <img alt="CKD-EPI" src={formulaCkdepiPng} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {resultCKDEPI.toFixed(1)}
              </span>{' '}
            </p>
            <Label className="text-foreground text-sm font-light">
              mL/min/1,73m²
            </Label>
          </div>

          <div className="flex flex-row items-center justify-center">
            <Label className="text-lg">MDRD</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-lg space-y-4">
                <h1 className="text-secondary text-center font-medium">MDRD</h1>
                <img alt="MDRD" src={formulaMdrdPng} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="mb-8 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {resultMDRD.toFixed(1)}
              </span>{' '}
            </p>
            <Label className="text-foreground text-sm font-light">
              mL/min/1,73m²
            </Label>
          </div>

          <p className="text-foreground text-center text-xs font-light">
            *Os cálculos acima são indicados para mensurar a TFG{' '}
            <span className="font-bold">em adultos</span>
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            Referência: Estimativa da taxa de filtração glomerular na prática
            clínica: posicionamento consensual da Sociedade Brasileira de
            Nefrologia (SBN) e Sociedade Brasileira de Patologia Clínica e
            Medicina Laboratorial (SBPC/ML) Braz. J. Nephrol. (J. Bras. Nefrol.)
            2024, 46(3):e20230193.
          </p>
        </form>
      </Form>
    </div>
  );
}

export default GlomerularFiltrationRate;
